import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';


const DropdownInputStyled = styled.input`
  padding: 0px 4px;
  width: 100%;
  box-sizing: border-box; 
  height: 30px; 
  font-size: 1.3rem; 
  font-family: 'Poppins', sans-serif; 
`;

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownListStyled = styled.ul`
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  background: #ffffff;
  border: 1px solid #ccc;
  border-radius: 6px;
  list-style: none;
  padding: 4px;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000; 
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  width: max-content;
  max-width: 300px;
`;

const DropdownListItemStyled = styled.li`
  padding: 4px 6px;
  cursor: pointer;
  font-size: 1.2rem;
  font-family: 'Poppins', sans-serif;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    background-color: #e9e9e9; // Slightly different hover for mouse
  }

  ${props =>
        props.isActive && // Conditional styling for keyboard active item
        css`
      background-color: #d0d0d0; // Or your preferred active color
      /* color: white; // if background is dark */
    `}

  &.no-results {
    cursor: default;
    color: #888;
    white-space: normal;
    &:hover {
      background-color: transparent;
    }
    ${props => props.isActive && css`background-color: transparent;`} // No active style for no-results
  }
`;

const SearchableDropdown = ({
    options,
    value,
    onChange,
    placeholder: customPlaceholder,
    noItemsText = 'No Items Found.',
    shouldFocus = false,
    containerRef,
    allowUnmatched = true,
    onUnmatchedInput = () => { } }) => {

    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    
    const internalListRef = useRef(null);
    const listRef = useRef(null);     // For scrolling the list
    const activeItemRef = useRef(null);
    const inputRef = useRef(null);

    const listContainerRef = useRef(null);

    const setListRefs = useCallback(node => {
        internalListRef.current = node;
        if (containerRef) {
            containerRef.current = node;
        }
    }, [containerRef]);

    // Filter options based on search query
    const filteredOptions = useMemo(() => {
        if (!searchQuery) {
            return options; // Show all options if search query is empty
        }
        return options.filter(option =>
            (option.name && option.name.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    }, [searchQuery, options]);

    // Handle input field changes
    const handleInputChange = (e) => {
        setSearchQuery(e.target.value);
        setIsOpen(true);
        setActiveIndex(-1); // Reset active index when search query changes
    };

    // Handle selection of an option
    const handleOptionSelect = (option) => {
        if (!option) return;
        onChange({ id: option.code, name: option.name });
        setIsOpen(false);
        setSearchQuery('');
        setActiveIndex(-1);
    };

    // Handle focus on input
    const handleInputFocus = () => {
        setIsOpen(true);
    };

    const validateAndCommit = () => {
        if (!searchQuery) {
            setIsOpen(false);
            return true; // Empty is valid
        }

        // Try to find an exact match for the typed code
        const exactMatch = options.find(opt => opt.code.toLowerCase() === searchQuery.toLowerCase());

        if (exactMatch) {
            handleOptionSelect(exactMatch);
            return true;
        }

        // If no exact match and unmatched values are disallowed
        if (!allowUnmatched) {
            onUnmatchedInput();
            return false;
        }

        setIsOpen(false);
        return true;
    };

    // Keyboard navigation handler
    const handleKeyDown = (e) => {
        if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            setIsOpen(true);
            return;
        }

        if (!isOpen) return;

        const optionsCount = filteredOptions.length;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (optionsCount > 0) {
                    setActiveIndex(prevIndex => (prevIndex + 1) % optionsCount);
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (optionsCount > 0) {
                    setActiveIndex(prevIndex => (prevIndex - 1 + optionsCount) % optionsCount);
                }
                break;
            case 'Enter':
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < optionsCount) {
                    handleOptionSelect(filteredOptions[activeIndex]);
                } else {
                    // If user hits Enter on a typed value
                    validateAndCommit();
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setActiveIndex(-1);
                break;
            case 'Tab':
                const isValid = validateAndCommit();
                if (!isValid) {
                    e.preventDefault();
                }
                break;
            default:
                break;
        }
    };

    const handleInputBlur = (e) => {
        if (!e.relatedTarget) {
            return;
        }

        if (e.relatedTarget && listContainerRef.current?.contains(e.relatedTarget)) {
            return;
        }
        setTimeout(() => {
            if (isOpen) {
                validateAndCommit();
            }
        }, 150);
    };

    //Focus on the input when edit row
    useEffect(() => {

        setTimeout(() => {
            if (shouldFocus && inputRef.current) {
                inputRef.current.focus();
            }
        }, 10);

    }, [shouldFocus]);

    // Effect to scroll the active item into view
    useEffect(() => {
        if (isOpen && activeItemRef.current && listRef.current) {
            const listItem = activeItemRef.current;
            const list = listRef.current;

            const listItemTop = listItem.offsetTop;
            const listItemBottom = listItemTop + listItem.offsetHeight;
            const listScrollTop = list.scrollTop;
            const listVisibleHeight = list.clientHeight;

            if (listItemTop < listScrollTop) { // Item is above the visible area
                list.scrollTop = listItemTop;
            } else if (listItemBottom > listScrollTop + listVisibleHeight) { // Item is below the visible area
                list.scrollTop = listItemBottom - listVisibleHeight;
            }
        }
    }, [activeIndex, isOpen, filteredOptions]); // Re-run if activeIndex or list changes


    const currentSelectedOption = options.find(opt => opt.code === value);
    const dynamicPlaceholder = currentSelectedOption
        ? `Current: ${currentSelectedOption.code}`
        : (customPlaceholder || "Type to search...");

    const displayValue = isOpen ? searchQuery : (value || '');

    return (
        <DropdownContainer>
            <DropdownInputStyled
                ref={inputRef}
                type="text"
                value={displayValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown} // Add keydown listener
                placeholder={dynamicPlaceholder}
                autoComplete="off"
            />
            {isOpen && (
                <DropdownListStyled ref={setListRefs}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => (
                            <DropdownListItemStyled
                                key={`${option.code}-${option.name}`}
                                onClick={() => {
                                    handleOptionSelect(option);
                                }}
                                onMouseEnter={() => setActiveIndex(index)}
                                isActive={index === activeIndex}
                                ref={index === activeIndex ? activeItemRef : null}
                            >
                                {option.name}
                            </DropdownListItemStyled>
                        ))
                    ) : (
                        <DropdownListItemStyled className="no-results" isActive={false}>
                            {noItemsText}
                        </DropdownListItemStyled>
                    )}
                </DropdownListStyled>
            )}
        </DropdownContainer>
    );
};

export default SearchableDropdown;